/**
 * Team Admin billing portal (FR-BILLING-01/02/03/05/06).
 *
 * Owns the page's data and every mutation; the tabs below it are presentational:
 *
 *   GET  /billing/v1/plans                      → catalog for the comparison
 *   GET  /billing/v1/subscriptions/{tenant_id}  → plan, entitlements, trial, usage
 *   POST /billing/v1/checkout-sessions          → hosted checkout (the card door)
 *   PATCH /billing/v1/subscriptions/{tenant_id} → downgrade / cancel-at-period-end
 *   GET  /billing/v1/portal                     → provider-hosted payment portal
 *
 * Upgrades leave the app for hosted checkout, so the subscription only changes
 * once the provider's webhook lands. On the return leg (`?upgrade=success`) the
 * page therefore polls the subscription for {@link UPGRADE_POLL_WINDOW_MS}
 * instead of claiming success it cannot see yet, and says plainly when the
 * confirmation is still in flight.
 *
 * Tab, invoice query and the deep-linked limit all live in the URL: a refresh,
 * a back button or a link from the tier-limit CTA lands on the same view.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { useToast } from '../../app/toast-context';
import { PageHeader } from '../../components/page-header';
import { ApiError } from '../../lib/api-error';
import type { BillingCadence, BillingPlan, BillingSubscriptionView } from '../../lib/api-types';
import { APP_VIEWS } from '../../lib/constants';

import { planFeatureLosses } from './billing-format';
import { InvoicesList } from './invoices-list';
import { PlanSelector } from './plan-selector';
import { TrialBanner } from './trial-banner';

/** How long the return-from-checkout poll waits for the provider webhook. */
export const UPGRADE_POLL_WINDOW_MS = 30_000;
export const UPGRADE_POLL_INTERVAL_MS = 5_000;

type BillingTab = 'plan' | 'invoices';

const TABS: readonly { id: BillingTab; label: string }[] = [
  { id: 'plan', label: 'Plan & usage' },
  { id: 'invoices', label: 'Invoices' },
];

/**
 * Full-page navigation seam. Hosted checkout and the provider portal are real
 * cross-origin redirects; tests replace this instead of stubbing `location`.
 */
export const billingNavigation = {
  redirect: (url: string): void => {
    globalThis.location?.assign(url);
  },
};

function humanMessage(caught: unknown, fallback: string): string {
  return ApiError.isApiError(caught) ? caught.message : fallback;
}

export interface BillingPortalProps {
  /** Defaults to the signed-in tenant (`tid` claim). */
  tenantId?: string;
}

export function BillingPortal({ tenantId: tenantIdProp }: BillingPortalProps = {}): JSX.Element {
  const apiClient = useApiClient();
  const { claims, session, tenantSlug } = useSession();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const tenantId = tenantIdProp ?? claims?.tid ?? session?.tenantId ?? null;
  const tab: BillingTab = searchParams.get('tab') === 'invoices' ? 'invoices' : 'plan';
  const highlightLimitKey = searchParams.get('limit');
  const isReturningFromCheckout = searchParams.get('upgrade') === 'success';

  const [view, setView] = useState<BillingSubscriptionView | null>(null);
  const [plans, setPlans] = useState<readonly BillingPlan[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Last failed mutation: the message plus how to try it again. */
  const [actionFailure, setActionFailure] = useState<{
    message: string;
    retry: (() => void) | null;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);
  const [isAwaitingUpgrade, setIsAwaitingUpgrade] = useState(isReturningFromCheckout);
  /** The window ran out before the provider confirmed: say so, offer a re-check. */
  const [isUpgradeStale, setIsUpgradeStale] = useState(false);
  /** Plan + trial/status fingerprint when the coach came back from checkout. */
  const stateAtReturnRef = useRef<string | null>(null);
  /** Fixed once per wait: every reload yields a new `view`, which re-runs the effect. */
  const awaitStartedAtRef = useRef<number | null>(null);

  const reload = useCallback((): void => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (tenantId === null) return undefined;
    const controller = new AbortController();
    setIsLoading(true);
    Promise.all([
      apiClient.getBillingSubscription(tenantId, controller.signal),
      apiClient.listBillingPlans(controller.signal),
    ])
      .then(([subscription, catalog]) => {
        setView(subscription);
        setPlans(catalog.plans);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(humanMessage(caught, 'Could not load your billing details.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [apiClient, tenantId, reloadToken]);

  // Return leg from hosted checkout: the subscription changes when the webhook
  // lands, not when the browser comes back, so poll for a bounded window.
  const clearUpgradeParam = useCallback((): void => {
    const params = new URLSearchParams(searchParams);
    if (!params.has('upgrade')) return;
    params.delete('upgrade');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!isAwaitingUpgrade || tenantId === null || view === null) return undefined;
    // A paid conversion shows up as a plan change OR (trial → paid on the same
    // plan, the trial banner's Upgrade) the trial ending / status changing.
    const fingerprint = `${view.entitlements.plan_id}|${view.entitlements.status}|${String(
      view.trial.is_trialing
    )}`;
    if (stateAtReturnRef.current === null) stateAtReturnRef.current = fingerprint;
    if (awaitStartedAtRef.current === null) awaitStartedAtRef.current = Date.now();
    if (fingerprint !== stateAtReturnRef.current) {
      setIsAwaitingUpgrade(false);
      clearUpgradeParam();
      showToast(`Your plan is now ${view.entitlements.plan_display_name}.`, 'success');
      return undefined;
    }
    if (Date.now() - awaitStartedAtRef.current > UPGRADE_POLL_WINDOW_MS) {
      setIsAwaitingUpgrade(false);
      setIsUpgradeStale(true);
      clearUpgradeParam();
      return undefined;
    }
    const timer = setTimeout(reload, UPGRADE_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [isAwaitingUpgrade, reload, showToast, tenantId, view, clearUpgradeParam]);

  const recheckUpgrade = useCallback((): void => {
    awaitStartedAtRef.current = null;
    setIsUpgradeStale(false);
    setIsAwaitingUpgrade(true);
    reload();
  }, [reload]);

  const setTab = useCallback(
    (next: BillingTab): void => {
      const params = new URLSearchParams(searchParams);
      params.set('tab', next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const returnPathBase = `/app/${tenantSlug ?? ''}/${APP_VIEWS.billing}`;

  const startCheckout = useCallback(
    async (plan: BillingPlan, cadence: BillingCadence): Promise<void> => {
      setActionFailure(null);
      try {
        const checkout = await apiClient.createCheckoutSession({
          plan_id: plan.plan_id,
          billing_cadence: cadence,
          success_path: `${returnPathBase}?tab=plan&upgrade=success`,
          cancel_path: `${returnPathBase}?tab=plan&upgrade=cancelled`,
        });
        billingNavigation.redirect(checkout.session_url);
      } catch (caught: unknown) {
        const message = humanMessage(
          caught,
          'Could not start checkout. Please try again in a moment.'
        );
        setActionFailure({ message, retry: () => void startCheckout(plan, cadence) });
        showToast(message, 'error');
      }
    },
    [apiClient, returnPathBase, showToast]
  );

  const changePlan = useCallback(
    async (plan: BillingPlan): Promise<boolean> => {
      if (tenantId === null) return false;
      setActionFailure(null);
      try {
        const updated = await apiClient.updateBillingSubscription(tenantId, {
          plan_id: plan.plan_id,
        });
        setView(updated);
        showToast(`Your plan is now ${updated.entitlements.plan_display_name}.`, 'success');
        return true;
      } catch (caught: unknown) {
        const message = humanMessage(caught, 'Could not change your plan. Please try again.');
        // `false` keeps the confirm dialog open with the message inline, so the
        // user keeps their intent and can retry in place.
        setActionFailure({ message, retry: null });
        return false;
      }
    },
    [apiClient, showToast, tenantId]
  );

  const cancelSubscription = useCallback(async (): Promise<boolean> => {
    if (tenantId === null) return false;
    setActionFailure(null);
    try {
      const updated = await apiClient.updateBillingSubscription(tenantId, {
        cancel_at_period_end: true,
      });
      setView(updated);
      showToast('Cancellation scheduled for the end of the current period.', 'success');
      return true;
    } catch (caught: unknown) {
      const message = humanMessage(caught, 'Could not cancel your subscription. Please try again.');
      setActionFailure({ message, retry: null });
      return false;
    }
  }, [apiClient, showToast, tenantId]);

  const openProviderPortal = useCallback(async (): Promise<void> => {
    setActionFailure(null);
    try {
      const portal = await apiClient.getBillingPortalSession(`${returnPathBase}?tab=plan`);
      billingNavigation.redirect(portal.url);
    } catch (caught: unknown) {
      const message = humanMessage(caught, 'The billing portal is unavailable right now.');
      setActionFailure({ message, retry: () => void openProviderPortal() });
      showToast(message, 'error');
    }
  }, [apiClient, returnPathBase, showToast]);

  const ordered = useMemo(
    () => [...plans].sort((left, right) => left.price_monthly_cents - right.price_monthly_cents),
    [plans]
  );
  const freePlan = ordered[0] ?? null;
  const trialPlan = plans.find((plan) => plan.plan_id === view?.entitlements.plan_id) ?? null;
  const trialLosses =
    view === null || freePlan === null
      ? []
      : planFeatureLosses(view.entitlements.features, freePlan.features);

  if (tenantId === null) {
    return (
      <section className="billing-portal">
        <div className="banner banner--error" role="alert">
          <p>
            This account is not attached to a team, so there is no subscription to manage. Sign in
            through your team portal to see billing.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="billing-portal">
      <PageHeader
        className="billing-portal__header"
        title="Billing"
        context={
          view === null
            ? 'Your plan, usage against its limits, and every invoice we have issued.'
            : `${view.entitlements.plan_display_name} plan · ${view.entitlements.status.replace(/_/gu, ' ')}`
        }
      />

      {view !== null && view.trial.is_trialing && view.trial.trial_ends_at !== null ? (
        <TrialBanner
          trialExpiresAt={view.trial.trial_ends_at}
          planName={view.entitlements.plan_display_name}
          downgradesToPlanName={freePlan?.display_name ?? 'Free'}
          lostFeatures={trialLosses}
          onUpgrade={() => {
            if (trialPlan === null) {
              setTab('plan');
              return;
            }
            void startCheckout(trialPlan, 'monthly');
          }}
          onComparePlans={() => {
            setTab('plan');
            document.getElementById('plan-comparison')?.scrollIntoView?.({ block: 'start' });
          }}
        />
      ) : null}

      {isAwaitingUpgrade ? (
        <div className="banner banner--success" role="status">
          <p>
            Payment received — we are waiting for the final confirmation from our payment provider.
            This page updates itself within about 30 seconds.
          </p>
        </div>
      ) : null}

      {isUpgradeStale ? (
        <div className="banner banner--warning" role="status" data-testid="billing-upgrade-stale">
          <p>
            Still waiting on the payment provider to confirm your upgrade. Your payment is safe —
            this usually finishes within a few minutes.
          </p>
          <button type="button" className="btn btn--secondary btn--sm" onClick={recheckUpgrade}>
            Check again
          </button>
        </div>
      ) : null}

      {searchParams.get('upgrade') === 'cancelled' ? (
        <div className="banner banner--warning" role="status">
          <p>Checkout was cancelled. Your plan has not changed.</p>
        </div>
      ) : null}

      <nav className="billing-tabs" aria-label="Billing sections">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className="billing-tabs__tab"
            aria-current={tab === entry.id ? 'page' : undefined}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      {isLoading && view === null ? (
        <p role="status">Loading your billing details…</p>
      ) : loadError !== null ? (
        <div className="banner banner--error" role="alert">
          <p className="banner__title">Billing is unavailable</p>
          <p>{loadError}</p>
          <div className="btn-row">
            <button type="button" className="btn btn--secondary" onClick={reload}>
              Try again
            </button>
          </div>
        </div>
      ) : tab === 'invoices' ? (
        <InvoicesList tenantId={tenantId} />
      ) : view === null ? null : (
        <PlanSelector
          view={view}
          plans={ordered}
          highlightLimitKey={highlightLimitKey}
          actionError={actionFailure?.message ?? null}
          onRetryAction={actionFailure?.retry ?? undefined}
          onUpgrade={startCheckout}
          onChangePlan={changePlan}
          onCancel={cancelSubscription}
          onManagePayment={openProviderPortal}
        />
      )}
    </section>
  );
}
